import { type FormEvent, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Sortable from "sortablejs";
import { useToast } from "../../../components/Toast";
import { request } from "../../../lib/api";
import type { CourseDetail, Lesson, LessonType, PresignUploadResponse } from "../../../lib/types";

const TAB_CSS = `
.lesson-row { display: flex; align-items: center; gap: 12px; padding: 12px; background: var(--c-bg); border: 1px solid var(--c-line-2); border-radius: 10px; margin-bottom: 8px; cursor: pointer; transition: all .15s; }
.lesson-row:hover { border-color: var(--c-primary); background: #fff; }
.lesson-row .drag-handle { color: var(--c-muted); cursor: grab; padding: 0 4px; }
.lesson-row .type-ico { width: 32px; height: 32px; border-radius: 8px; display: grid; place-items: center; flex-shrink: 0; }
.type-text { background: var(--c-primary-light); color: var(--c-primary-dark); }
.type-video { background: #fde6eb; color: var(--c-accent); }
.type-mixed { background: #fdf1de; color: #8a5a04; }
.lesson-row.sortable-ghost { opacity: 0.4; }
.upload-zone { border: 2px dashed var(--c-line); border-radius: 10px; padding: 32px; text-align: center; color: var(--c-muted); transition: all .15s; cursor: pointer; }
.upload-zone:hover, .upload-zone.dragging { border-color: var(--c-primary); background: var(--c-primary-50); color: var(--c-primary-dark); }
.upload-progress { height: 8px; background: var(--c-line); border-radius: 4px; overflow: hidden; margin-top: 12px; }
.upload-progress > span { display: block; height: 100%; background: var(--c-primary); transition: width .2s; }
`;

export function LessonsTab({ course }: { course: CourseDetail }) {
  const qc = useQueryClient();
  const toast = useToast();
  const lessons = (course.lessons || []).slice().sort((a, b) => a.position - b.position);
  const [selectedId, setSelectedId] = useState<string | null>(lessons[0]?.id || null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!listRef.current) return;
    const s = Sortable.create(listRef.current, {
      animation: 150,
      handle: ".drag-handle",
      onEnd: async () => {
        const ids = Array.from(listRef.current!.querySelectorAll<HTMLElement>("[data-lesson]")).map((el) => el.dataset.lesson!);
        try {
          await request(`/courses/${course.id}/lessons/reorder`, { method: "POST", body: { ordered_ids: ids } });
          qc.invalidateQueries({ queryKey: ["course", course.id] });
          toast("Ordinea salvată", "success");
        } catch (err) {
          toast(err instanceof Error ? err.message : "Eroare la reordonare", "error");
        }
      },
    });
    return () => s.destroy();
  }, [course.id, qc, toast, lessons.length]);

  const addLesson = useMutation({
    mutationFn: (type: LessonType) => {
      const nextPos = (lessons[lessons.length - 1]?.position || 0) + 1;
      return request<Lesson>(`/courses/${course.id}/lessons`, {
        method: "POST",
        body: { title: `Lecție nouă ${nextPos}`, position: nextPos, type, duration_minutes: 5 },
      });
    },
    onSuccess: (l) => {
      toast("Lecție adăugată", "success");
      setSelectedId(l.id);
      qc.invalidateQueries({ queryKey: ["course", course.id] });
    },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });

  const selected = lessons.find((l) => l.id === selectedId) || null;

  return (
    <>
      <style>{TAB_CSS}</style>
      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 24 }}>
        <div>
          <div className="card card-pad-sm" style={{ position: "sticky", top: 90 }}>
            <div className="card-head"><h3 style={{ margin: 0, fontSize: "1rem" }}>Structură curs</h3><small className="muted">{lessons.length} lecții</small></div>
            <div ref={listRef}>
              {lessons.map((l, i) => {
                const isActive = l.id === selectedId;
                const typeCls = l.type === "video" ? "type-video" : (l.type === "mixed" ? "type-mixed" : "type-text");
                const typeIcon = l.type === "video" ? "fa-video" : (l.type === "mixed" ? "fa-shapes" : "fa-file-lines");
                return (
                  <div
                    key={l.id}
                    data-lesson={l.id}
                    className="lesson-row"
                    style={isActive ? { borderColor: "var(--c-primary)", background: "var(--c-primary-light)" } : undefined}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest(".drag-handle")) return;
                      setSelectedId(l.id);
                    }}
                  >
                    <span className="drag-handle"><i className="fa-solid fa-grip-vertical"></i></span>
                    <div className={"type-ico " + typeCls}><i className={`fa-solid ${typeIcon}`}></i></div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: ".88rem", display: "block" }}>{i + 1}. {l.title}</strong>
                      <small className="muted">{l.duration_minutes ? l.duration_minutes + " min" : "—"}</small>
                    </div>
                  </div>
                );
              })}
              {lessons.length === 0 && <div className="text-muted" style={{ padding: 12, textAlign: "center" }}>Nicio lecție.</div>}
            </div>
            <div className="divider"></div>
            <strong style={{ fontSize: ".85rem", display: "block", marginBottom: 10 }}>Adaugă lecție</strong>
            <div className="flex gap-8" style={{ flexWrap: "wrap" }}>
              <button className="btn btn-ghost btn-sm" onClick={() => addLesson.mutate("text")}><i className="fa-solid fa-file-lines"></i> Text</button>
              <button className="btn btn-ghost btn-sm" onClick={() => addLesson.mutate("video")}><i className="fa-solid fa-video"></i> Video</button>
              <button className="btn btn-ghost btn-sm" onClick={() => addLesson.mutate("mixed")}><i className="fa-solid fa-shapes"></i> Mixt</button>
            </div>
          </div>
        </div>
        <div>{selected ? <LessonEditor course={course} lesson={selected} onDelete={() => setSelectedId(lessons[0]?.id || null)} /> : <div className="card text-muted">Selectează sau adaugă o lecție.</div>}</div>
      </div>
    </>
  );
}

function LessonEditor({ course, lesson, onDelete }: { course: CourseDetail; lesson: Lesson; onDelete: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState(lesson.title);
  const [type, setType] = useState<LessonType>(lesson.type);
  const [duration, setDuration] = useState<number | null>(lesson.duration_minutes);
  const [text, setText] = useState(lesson.text_content || "");
  const [required, setRequired] = useState(lesson.is_required);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Sync local state when the underlying lesson changes (e.g., after a save+refetch).
  useEffect(() => {
    setTitle(lesson.title);
    setType(lesson.type);
    setDuration(lesson.duration_minutes);
    setText(lesson.text_content || "");
    setRequired(lesson.is_required);
  }, [lesson.id, lesson.title, lesson.type, lesson.duration_minutes, lesson.text_content, lesson.is_required]);

  const save = useMutation({
    mutationFn: () => request<Lesson>(`/lessons/${lesson.id}`, {
      method: "PATCH",
      body: { title, type, duration_minutes: duration, text_content: text || null, is_required: required },
    }),
    onSuccess: () => {
      toast("Lecție salvată", "success");
      qc.invalidateQueries({ queryKey: ["course", course.id] });
    },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });

  const del = useMutation({
    mutationFn: () => request(`/lessons/${lesson.id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("Lecție ștearsă", "success");
      qc.invalidateQueries({ queryKey: ["course", course.id] });
      onDelete();
    },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });

  const detach = useMutation({
    mutationFn: () => request<Lesson>(`/lessons/${lesson.id}`, { method: "PATCH", body: { video_asset_id: null } }),
    onSuccess: () => { toast("Video detașat", "success"); qc.invalidateQueries({ queryKey: ["course", course.id] }); },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });

  async function uploadVideo(file: File) {
    if (file.size > 500 * 1024 * 1024) { toast("Fișierul depășește 500MB", "error"); return; }
    setUploadPct(5);
    try {
      const presign = await request<PresignUploadResponse>("/media/videos/presign", {
        method: "POST",
        body: { filename: file.name, content_type: file.type || "video/mp4" },
      });
      setUploadPct(15);

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", presign.upload_url);
        xhr.setRequestHeader("Content-Type", file.type || "video/mp4");
        xhr.upload.addEventListener("progress", (evt) => {
          if (evt.lengthComputable) setUploadPct(15 + Math.round((evt.loaded / evt.total) * 75));
        });
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error("Upload eșuat HTTP " + xhr.status));
        xhr.onerror = () => reject(new Error("Eroare rețea la upload"));
        xhr.send(file);
      });
      setUploadPct(95);

      await request(`/media/videos/${presign.asset_id}/complete`, {
        method: "POST",
        body: { size_bytes: file.size },
      });

      await request(`/lessons/${lesson.id}`, {
        method: "PATCH",
        body: { video_asset_id: presign.asset_id },
      });

      setUploadPct(100);
      qc.invalidateQueries({ queryKey: ["course", course.id] });
      toast("Video încărcat", "success");
      setTimeout(() => setUploadPct(null), 1500);
    } catch (err) {
      toast(err instanceof Error ? err.message : "Upload eșuat", "error");
      setUploadPct(null);
    }
  }

  function onZoneDrop(e: React.DragEvent) {
    e.preventDefault();
    (e.currentTarget as HTMLElement).classList.remove("dragging");
    const f = e.dataTransfer.files[0];
    if (f) uploadVideo(f);
  }

  const statusLabel: Record<string, string> = {
    pending: "În așteptare",
    uploaded: "În coadă transcodare",
    transcoding: "Se procesează…",
    ready: "Pregătit (HLS)",
    failed: "Eșuat",
  };

  const showVideo = type === "video" || type === "mixed";

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <small className="muted">Editează lecția</small>
          <h3 style={{ margin: "2px 0 0" }}>{lesson.title}</h3>
        </div>
        <button className="btn btn-accent btn-sm" onClick={() => { if (confirm("Ștergi această lecție?")) del.mutate(); }}>
          <i className="fa-solid fa-trash"></i>
        </button>
      </div>
      <div className="form-group"><label>Titlu</label><input className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
      <div className="grid-2">
        <div className="form-group">
          <label>Tip</label>
          <select className="form-control" value={type} onChange={(e) => setType(e.target.value as LessonType)}>
            <option value="text">📄 Text</option>
            <option value="video">🎬 Video</option>
            <option value="mixed">📄 + 🎬 Mixt</option>
          </select>
        </div>
        <div className="form-group"><label>Durată (min)</label><input className="form-control" type="number" value={duration ?? ""} onChange={(e) => setDuration(e.target.value ? Number(e.target.value) : null)} /></div>
      </div>

      {showVideo && (
        <div className="form-group">
          <label>Video</label>
          <div
            className="upload-zone"
            onClick={() => fileRef.current?.click()}
            onDragEnter={(e) => { e.preventDefault(); (e.currentTarget as HTMLElement).classList.add("dragging"); }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={(e) => (e.currentTarget as HTMLElement).classList.remove("dragging")}
            onDrop={onZoneDrop}
          >
            <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: "2rem", color: "var(--c-primary)", marginBottom: 10 }}></i>
            <div style={{ fontWeight: 600, color: "var(--c-ink)" }}>Apasă sau glisează un fișier video</div>
            <small>MP4, MOV, WebM · max 500MB</small>
            <input ref={fileRef} type="file" accept="video/*" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadVideo(f); }} />
          </div>
          {uploadPct !== null && <div className="upload-progress"><span style={{ width: uploadPct + "%" }}></span></div>}
          {lesson.video_asset && (
            <div className="flex gap-12 mt-16" style={{ padding: 14, background: "var(--c-bg)", borderRadius: 10, border: "1px solid var(--c-line-2)" }}>
              <i className="fa-solid fa-circle-play" style={{ color: "var(--c-primary)", fontSize: "1.6rem" }}></i>
              <div style={{ flex: 1 }}>
                <strong style={{ fontSize: ".92rem" }}>Video asociat</strong><br />
                <small className="muted">status: {statusLabel[lesson.video_asset.status] || lesson.video_asset.status}</small>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => detach.mutate()}><i className="fa-solid fa-trash"></i></button>
            </div>
          )}
        </div>
      )}

      <div className="form-group"><label>Conținut text</label><textarea className="form-control" style={{ minHeight: 180 }} value={text} onChange={(e) => setText(e.target.value)} /></div>

      <div className="flex flex-between mt-16" style={{ borderTop: "1px solid var(--c-line)", paddingTop: 18 }}>
        <label className="form-check"><input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} /> Necesită finalizare obligatorie</label>
        <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
          <i className="fa-solid fa-floppy-disk"></i> {save.isPending ? "Se salvează..." : "Salvează lecția"}
        </button>
      </div>
    </div>
  );
}
